import { AddIcon } from '@systemeio/ui/icons'
import { FormSelect, Hint, FormInputNumber } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { useSaleLimit } from 'modules/funnels/funnel/components/step-configuration-tab/sale-limit/hooks/use-sale-limit'
import {
  SaleLimitEditInterface,
  SaleLimitViewInterface,
} from 'modules/funnels/funnel/components/step-configuration-tab/sale-limit/types/sale-limit-interface'
import RemoveIcon from 'modules/funnels/funnel/icons/remove-icon'
import TrashIcon from 'modules/funnels/funnel/icons/trash-icon'
import { FunnelStepId } from 'modules/funnels/funnel/types/funnel-step-interface'
import { ListingDataInterface } from 'modules/funnels/funnel/utils/offer-configuration-utils'
import React, { useEffect, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { useActOnBlur } from 'shared/hooks/use-act-on-blur'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface SaleLimitProps extends SaleLimitViewInterface {
  saleLimitFunnelStepData: ListingDataInterface[]
  funnelStepId: FunnelStepId
  onSave: (data: SaleLimitEditInterface) => Promise<void>
}

const defaultValues: SaleLimitEditInterface = {
  saleLimit: null,
  saleLimitFunnelStepRedirect: null,
}

function SaleLimit({
  saleLimit,
  saleLimitFunnelStepRedirect,
  saleLimitFunnelStepData,
  funnelStepId,
  onSave,
}: SaleLimitProps) {
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState(defaultValues)
  const [isOpen, setIsOpen] = useState(false)
  const { saleLimitData } = useSaleLimit(funnelStepId, !!saleLimit)
  const [modalOpened, setModalOpened] = useState(false)

  const handleError = (error: Error) => {
    if (error instanceof BadRequest) {
      const errors = error.errors.fields
      salesLimitActor.setStatus(errors.saleLimit?.join(', '))
      saleLimitFunnelStepRedirectActor.setStatus(
        errors.saleLimitRedirectStep?.join(', ') || errors.saleLimitFunnelStepRedirect?.join(', '),
      )
    }
  }

  const salesLimitActor = useActOnBlur<SaleLimitEditInterface['saleLimit']>(() => ({
    action: async value => {
      await onSave({
        ...tempState,
        saleLimit: value,
      })
    },
    onError: handleError,
  }))

  const saleLimitFunnelStepRedirectActor = useActOnBlur<
    SaleLimitEditInterface['saleLimitFunnelStepRedirect']
  >(() => ({
    action: async value => {
      await onSave({
        ...tempState,
        saleLimitFunnelStepRedirect: value,
      })
    },
    onError: handleError,
  }))

  useEffect(() => {
    if (saleLimit || saleLimitFunnelStepRedirect) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
    setTempState({
      saleLimit,
      saleLimitFunnelStepRedirect: saleLimitFunnelStepRedirect?.id,
    })
  }, [saleLimit, saleLimitFunnelStepRedirect])

  const onChangeSaleLimitFunnelStep = async (data?: number) => {
    const funnelStep = saleLimitFunnelStepData.find(item => item.id === data)
    const newData = { ...tempState, saleLimitFunnelStepRedirect: funnelStep?.id || null }
    setTempState(newData)
    await saleLimitFunnelStepRedirectActor.handleBlur(newData.saleLimitFunnelStepRedirect)
  }

  const onRemove = async () => {
    const newData = { saleLimit: null, saleLimitFunnelStepRedirect: null }
    await onSave(newData).catch(handleError)
    setIsOpen(false)
    setTempState(newData)
  }

  return (
    <>
      <div className="flex items-center">
        {isOpen ? (
          <RemoveIcon
            className={`fill-brand-primary mr-3 h-[18px] w-[18px] cursor-pointer`}
            onClick={() => setIsOpen(false)}
          />
        ) : (
          <AddIcon
            className={`fill-brand-primary mr-3 h-[18px] w-[18px] cursor-pointer`}
            onClick={() => setIsOpen(true)}
          />
        )}
        <span className={'text-text-primary text-sm font-normal lg:text-[15px]'}>
          <Hint label={t('dashboard.funnel.configuration.offer.sales_limit_popover')}>
            {t('dashboard.funnel.configuration.offer.sales_limit')}
          </Hint>
        </span>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4 xl:flex-row  xl:gap-5">
          <div className="flex flex-col justify-center gap-1">
            <FormInputNumber
              type={'integer'}
              label={t('dashboard.funnel.configuration.offer.sales_limit')}
              labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
              required
              value={tempState.saleLimit === null ? 0 : tempState.saleLimit}
              onChange={value => setTempState(prev => ({ ...prev, saleLimit: value }))}
              onBlur={(_, processedNumber) => salesLimitActor.handleBlur(processedNumber)}
              maxLength={256}
              statusIcon={salesLimitActor.statusIcon}
              error={salesLimitActor.error}
            />
            {!!saleLimit && (
              <>
                {saleLimitData ? (
                  <div className="flex justify-center">
                    {t('dashboard.funnel.configuration.offer.sales_limit_sold', {
                      salesCount: saleLimitData.salesCount,
                      salesLimit: saleLimit,
                    })}
                  </div>
                ) : (
                  <div
                    className={'bg-skeleton-primary mx-4 animate-pulse rounded-lg text-transparent'}
                  >
                    .
                  </div>
                )}
              </>
            )}
          </div>
          <FormSelect
            label={t('dashboard.funnel.configuration.offer.redirect_funnel_step')}
            labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
            value={tempState.saleLimitFunnelStepRedirect || undefined}
            data={saleLimitFunnelStepData}
            onChange={onChangeSaleLimitFunnelStep}
            required
            statusIcon={saleLimitFunnelStepRedirectActor.statusIcon}
            error={saleLimitFunnelStepRedirectActor.error}
          />
          <TrashIcon
            className="h-[18px] w-[17px] cursor-pointer"
            onClick={() => setModalOpened(true)}
          />
        </div>
      )}
      <ConfirmationModal
        confirmationText={t('dashboard.funnel.configuration.offer.remove_offer_sale_limit')}
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onConfirm={onRemove}
      />
    </>
  )
}

export default SaleLimit
